/* eslint-disable css-modules/no-undef-class */
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";

import Slider, { Settings as SlickSettings } from "react-slick";
import { MoreProducts } from "web/react/components/product-gallery/product-gallery-more-products/product-gallery-more-products";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import styles from "web/react/pages/product/product-image-gallery/product-image-carousel-emo.module.css";

import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { Image, ProductGalleryImage } from "./product-gallery-image";

export interface ProductImageGalleryProps {
    images: Image[];
    isFullscreen?: boolean;
    settings?: SlickSettings;
    handleImageClick?: (event: React.SyntheticEvent, index: number) => void;
    onClose: () => void;
    currentSlideIndex: number;
    productId: string;
    isInStockProductPage?: boolean;
}

export function ProductImageGalleryCarouselEmo({
    images,
    isFullscreen,
    settings,
    handleImageClick,
    onClose,
    currentSlideIndex,
    productId,
    isInStockProductPage = false,
}: ProductImageGalleryProps): React.ReactElement {
    const [seenAll, setSeenAll] = useState(false);
    const [showExpand, setShowExpand] = useState(true);

    const { isMobileViewport, isTabletViewport, isDesktopViewport } = useDomViewport();
    const { relatedProducts: moreLikeThisProducts, productPurchaseType } =
        useInStockProductPageContext();

    const isExpressCheckout = productPurchaseType === ProductPurchaseType.EXPRESS_CHECKOUT;
    const isLoadingExperimentEnabled = !!(
        isInStockProductPage && environment.getFeature("beam_web_pdp_gallery_fullscreen_loading")
    );

    const shouldLazyLoad = useCallback(
        (imageIndex: number): boolean => {
            if (isLoadingExperimentEnabled) {
                // Flag enabled: lazy load all fullscreen images and all images after the first in non-fullscreen mode.
                return isFullscreen || imageIndex !== 0;
            }
            // Flag disabled: lazy load images only if in the desktop viewport.
            return isDesktopViewport;
        },
        [isLoadingExperimentEnabled, isFullscreen, isDesktopViewport]
    );

    const carouselImages = images.map((image, index) => {
        return (
            <ProductGalleryImage
                lazy={shouldLazyLoad(index)}
                key={`${image.id}-${index}`}
                src={image.url}
                alt={image.altText ?? undefined}
                onClick={(event) => {
                    onImageClick(event, index);
                }}
                onKeyDown={(event) => {
                    onImageClick(event, index);
                }}
                isFullscreen={isFullscreen}
                blurImageOnLoad={isLoadingExperimentEnabled}
            />
        );
    });

    if (isMobileViewport && moreLikeThisProducts && moreLikeThisProducts.length > 0) {
        carouselImages.push(
            <MoreProducts
                key="related-slide"
                products={moreLikeThisProducts.slice(0, 4)}
                fullscreen={isFullscreen}
            />
        );
    }

    const lastImageIndex = carouselImages.length - 1;
    const total = carouselImages.length;
    settings = { ...settings, dots: total > 1 };

    // Component for the gallery thumbnail image so we can lazy-load it client-side instead
    function ThumbnailImage({ image }: { image: string }): React.ReactElement | null {
        const [src, setSrc] = React.useState(
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        );

        React.useEffect(() => {
            if ((isTabletViewport || isDesktopViewport) && isFullscreen) {
                setSrc(image);
            }
        }, [image]);

        if ((isTabletViewport || isDesktopViewport) && isFullscreen) {
            return <img src={src} alt="" />;
        } else {
            return null;
        }
    }

    const sliderRef = useRef<any>(null);

    function onImageClick(event, index): void {
        if (isFullscreen) {
            return;
        }
        handleImageClick && handleImageClick(event, index);
    }

    function getDots(dots): React.ReactElement {
        const thumbnails = dots.map((dot, index) => {
            const image: Image | undefined = images[index];

            return (
                <li key={`${dot.key}-${index}`} className={dot.props.className}>
                    <button
                        data-testid={`dots-${index}`}
                        onClick={(e) => {
                            dot.props.children.props.onClick(e);
                        }}
                    >
                        {image && <ThumbnailImage image={image.url} />}
                    </button>
                </li>
            );
        });

        const noImageDots = dots.map((dot, index) => {
            return (
                isFullscreen && (
                    <li
                        key={`${dot.key}-${index}`}
                        className={clsx(dot.props.className, {
                            [styles.noImageDots]: isFullscreen,
                        })}
                    >
                        <button
                            data-testid={`dot-${index}`}
                            onClick={(e) => {
                                dot.props.children.props.onClick(e);
                            }}
                        ></button>
                    </li>
                )
            );
        });

        return (
            <div>
                <ul
                    className={clsx(styles.imageDotsUl, {
                        [styles.longerSlides]: total >= 6,
                    })}
                >
                    {thumbnails}
                </ul>
                <ul className={styles.noImageDotsUl}>{noImageDots}</ul>
            </div>
        );
    }

    const onKeyDown = (e: KeyboardEvent): void => {
        e.preventDefault();

        if (e.key === "Left" || e.key === "ArrowLeft" || e.key === "Up" || e.key === "ArrowUp") {
            sliderRef.current.slickPrev();
        }

        if (
            e.key === "Right" ||
            e.key === "ArrowRight" ||
            e.key === "Down" ||
            e.key === "ArrowDown"
        ) {
            sliderRef.current.slickNext();
        }

        if (e.key === "Escape") {
            onClose();
        }
    };

    function afterSlideChange(currentSlide): void {
        if (seenAll) {
            // return so we only track "seen_all" once
            return;
        } else if (currentSlide === lastImageIndex) {
            setSeenAll(true);
            analytics.event("product_gallery", "engagement", "seen_all", false, {
                product_id: productId,
            });
        }
    }

    useEffect(() => {
        if (isFullscreen) {
            document.addEventListener("keydown", onKeyDown);

            return () => {
                document.removeEventListener("keydown", onKeyDown);
            };
        }
    });

    const sliderClasses = clsx({
        [styles.fullscreen]: isFullscreen,
    });

    return (
        <RemoveScroll enabled={isFullscreen}>
            {/* TODO This style can be removed when we rewrite the CSS to not use :global */}
            <div data-testid="image-gallery" className={styles.emo}>
                {total >= 1 && (
                    <button
                        className={clsx(styles.arrowLeft, {
                            [styles.fullscreenArrowLeft]: isFullscreen,
                            [styles.active]: currentSlideIndex !== 0 && total !== 0,
                        })}
                        onClick={() => {
                            currentSlideIndex !== 0 && sliderRef.current.slickPrev();
                        }}
                        data-testid="chevron-thin-left"
                    >
                        <SVGIcon name={"chevron-thin"} rotate={90} />
                    </button>
                )}
                <Slider
                    ref={sliderRef}
                    className={sliderClasses}
                    afterChange={afterSlideChange}
                    beforeChange={(_, nextSlide) => {
                        setShowExpand(
                            isTabletViewport ||
                                (isMobileViewport && !(nextSlide === lastImageIndex))
                        );
                    }}
                    {...settings}
                    adaptiveHeight={isMobileViewport && isExpressCheckout}
                    appendDots={getDots}
                >
                    {carouselImages}
                </Slider>
                <span className={styles.expandIconContainer}>
                    <button onClick={(event) => handleImageClick?.(event, currentSlideIndex)}>
                        <SVGIcon
                            name="expand"
                            className={clsx(styles.expandIcon, {
                                hidden: isFullscreen || !showExpand,
                            })}
                        />
                    </button>
                </span>
                {total >= 1 && (
                    <button
                        className={clsx(styles.arrowRight, {
                            [styles.active]: currentSlideIndex !== lastImageIndex && total !== 0,
                            [styles.fullscreenArrowRight]: isFullscreen,
                        })}
                        onClick={() => {
                            currentSlideIndex !== lastImageIndex && sliderRef.current.slickNext();
                        }}
                        data-testid="chevron-thin-right"
                    >
                        <SVGIcon name={"chevron-thin"} rotate={-90} />
                    </button>
                )}
            </div>
        </RemoveScroll>
    );
}
